// dependency-graph-ignore-file
window.global ||= window; // Ensures that `global` is defined in the browser for libraries that require it.

import {
  currentBridgeData,
  initializeBridgeData,
} from '@databases/database-state/state/bridge-data';
import { mountDefaultFlagGroup } from '@databases/flags';
import { getAppModeFromQueryParams } from '@databases/modes';
import {
  initNativeEmbedEditorPlugin,
  sendContentReady,
} from '@databases/native-embed-editor-plugin';
import { container } from '@databases/service-container';
import React from 'react';

import { createBridgeDatabaseLoadDispatchRecyclable } from '../bootstrap/bridge-identity/create-bridge-database-load-dispatch-recyclable';
import { createClearBridgeFlagsRecyclable } from '../bootstrap/bridge-identity/create-clear-bridge-flags-recyclable';
import { createCloseConfluenceSidePanelRecyclable } from '../bootstrap/bridge-identity/create-close-confluence-side-panel-recyclable';
import { createPublishEmptyDatabaseReferencingRecyclable } from '../bootstrap/bridge-identity/create-publish-empty-database-referencing-recyclable';
import { tryDispatchDatabaseLoadFromCurrentBridge } from '../bootstrap/bridge-identity/try-dispatch-database-load-from-current-bridge';
import { trySdkDatabaseLoadFromCurrentBridge } from '../bootstrap/bridge-identity/try-sdk-database-load-from-current-bridge';
import { setupCommentsPanelBridgeListeners } from '../bootstrap/comments-panel-listener';
import { createFirstEditAnalyticsRecyclable } from '../bootstrap/create-first-edit-analytics-recyclable';
import { getModeConfig } from '../bootstrap/get-mode-config';
import { initializeCurrentDatabaseSdk } from '../bootstrap/initialize-current-database-sdk';
import { getThemeStateFromQueryParams } from '../bootstrap/initialize-database-theme';
import { initializeDatabasesRovoBridge } from '../bootstrap/initialize-databases-rovo-bridge';
import { initializePermissionsManager } from '../bootstrap/initialize-permissions-manager';
import { initializeRecyclableSelectionsMachine } from '../bootstrap/initialize-recyclable-selections-machine';
import { initializeTableHighlightsController } from '../bootstrap/initialize-table-highlights-controller';
import { preloadToolbar } from '../bootstrap/preload-toolbar';
import { registerPanelsForPanelRenderer } from '../bootstrap/register-panels-for-panel-renderer';
import { registerRecyclableModules } from '../bootstrap/register-recyclable-modules';
import { resolveSdkDatabaseLoad } from '../bootstrap/resolve-sdk-database-load';

import { tryInitializeNativeEmbedBridge } from './bridge/try-initialize-native-embed-bridge';
import AppWithLocale from './components/app-with-locale';
import { createDatabasesApp } from './create-databases-app';

// Loading waterfall:
//
//   initializeBridgeData()        — resolves cloudId, contentId, token from bridge
//         │
//         ├── sdkLoadPromise      — registered here, but the WebSocket connect is
//         │                         internally gated on `featureFlagsInitialized`
//         │                         so we route to the correct collab URL. This
//         │                         still overlaps most of intl fetching.
//         │
//         └── bootstrap()         — loads intl messages + feature flags (network-bound)
//                │
//                └── resolveSdkDatabaseLoad()  — awaits the SDK promise, injects the
//                                               loaded database directly into Redux
//                                               (skipping PENDING state)
//
// If the SDK loader fails for any reason, we fall back to the legacy Redux thunk
// path which goes through the full PENDING → SUCCEEDED loading state lifecycle.

const run = createDatabasesApp(async ({ bootstrap, mount }) => {
  const { cloudId, spaceId, identity, contentId, referencing } =
    await initializeBridgeData();

  // Kick off the SDK collab load. Internally it awaits feature flags before
  // opening the socket (the collab URL is flag-gated), then races the WebSocket
  // handshake against the remaining bootstrap work.
  const sdkLoadPromise = trySdkDatabaseLoadFromCurrentBridge();

  const appMode = getAppModeFromQueryParams(new URLSearchParams(window.location.search));
  const modeConfig = getModeConfig(appMode);

  await bootstrap({
    appMode,
    cloudId,
    identity,
    dependencies: modeConfig.dependencies,
    referencing,
    spaceId,
    themeState: getThemeStateFromQueryParams(),
  });

  // Resolve the SDK load now that bootstrap has finished (intl is available for
  // migrations). On success, the database is injected into Redux ready-to-render.
  const sdkLoadSucceeded = sdkLoadPromise
    ? await resolveSdkDatabaseLoad(sdkLoadPromise, {
        contentId: currentBridgeData.data.contentId,
        contentReferenceId: currentBridgeData.data.contentReferenceId,
        spaceId: currentBridgeData.data.spaceId,
        spaceKey: currentBridgeData.data.spaceKey,
        spaceName: currentBridgeData.data.spaceName,
        title: currentBridgeData.data.title,
      })
    : false;

  if (!sdkLoadSucceeded) {
    tryDispatchDatabaseLoadFromCurrentBridge();
  }

  preloadToolbar();
  registerPanelsForPanelRenderer();

  if (appMode === 'NATIVE_EMBED') {
    await tryInitializeNativeEmbedBridge(() => currentBridgeData.data);
  }

  if (appMode === 'NATIVE_EMBED_EDITOR_PLUGIN') {
    initNativeEmbedEditorPlugin();

    container.resolve('EventBus').on('databaseUILoaded', () => {
      sendContentReady();
    });
  }

  registerRecyclableModules(
    [
      createBridgeDatabaseLoadDispatchRecyclable(),
      createFirstEditAnalyticsRecyclable(),
      createClearBridgeFlagsRecyclable(),
      createCloseConfluenceSidePanelRecyclable(),
      createPublishEmptyDatabaseReferencingRecyclable(),
      initializeCurrentDatabaseSdk(),
      initializeDatabasesRovoBridge(),
      initializePermissionsManager(),
      setupCommentsPanelBridgeListeners(),
      initializeTableHighlightsController(),
      initializeRecyclableSelectionsMachine(),
    ],
    contentId,
  );

  mount((getRoot) => {
    const root = getRoot();

    root.render(<AppWithLocale />);

    mountDefaultFlagGroup(() => container.resolve('FlagsManager'));
  });
});

run();
